import { apiFetch } from './client';

export type PrintDestinationDTO = {
    id: number;
    code: string;
    name: string;
    destinationType: string;
    connectionType?: string | null;
    connectionConfig?: unknown;
    enabled: boolean;
    isDefault: boolean;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type PrintTemplateDTO = {
    id: number;
    code: string;
    name: string;
    description?: string | null;
    templateType: string;
    locale: string;
    payloadSchemaVersion: number;
    content: string;
    footer?: string | null;
    active: boolean;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type PrintJobDTO = {
    id: number;
    tenantId?: number | null;
    destinationId?: number | null;
    destinationCode?: string | null;
    destinationType?: string | null;
    templateId?: number | null;
    templateCode?: string | null;
    status?: string | null;
    attempts?: number | null;
    priority?: number | null;
    payload?: string | null;
    renderedContent?: string | null;
    errorMessage?: string | null;
    orderCode?: string | null;
    sourceId?: number | null;
    sourceType?: string | null;
    createdAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
};

export type PrintPreviewTemplateDescriptor = {
    id?: number | null;
    code?: string | null;
    name?: string | null;
    templateType?: string | null;
    content?: string | null;
    footer?: string | null;
};

export type TenantBrandingDTO = {
    tenantName?: string | null;
    mainColor?: string | null;
    accentColor?: string | null;
    primaryTextColor?: string | null;
    secondaryTextColor?: string | null;
    logoUrl?: string | null;
};

export type PrintPreviewDTO = {
    orderId?: number | null;
    orderCode?: string | null;
    channel: string;
    destinationType: string;
    locale: string;
    generatedAt: string;
    payload: Record<string, unknown>;
    template?: PrintPreviewTemplateDescriptor | null;
    branding?: TenantBrandingDTO | null;
    renderedHtml?: string | null;
};

export type PrintPreviewParams = {
    channel: string;
    destinationType?: string;
    templateId?: number | null;
};

export type PrintDestinationPayload = {
    code: string;
    name: string;
    destinationType: string;
    connectionType?: string | null;
    connectionConfig?: unknown;
    enabled?: boolean;
    isDefault?: boolean;
};

export type PrintTemplatePayload = {
    code: string;
    name: string;
    description?: string | null;
    templateType: string;
    locale: string;
    payloadSchemaVersion?: number;
    content: string;
    footer?: string | null;
    active?: boolean;
};

export type ManualPrintPayload = {
    destinationId: number;
    templateId?: number | null;
    priority?: number | null;
    channel?: string;
};

async function handleResponse<T>(res: Response, fallback: string): Promise<T> {
    if (res.ok) {
        if (res.status === 204) {
            return undefined as T;
        }
        return res.json() as Promise<T>;
    }
    let message = fallback;
    try {
        const body = await res.text();
        if (body) {
            try {
                const parsed = JSON.parse(body);
                if (parsed && typeof parsed === 'object' && 'error' in parsed) {
                    const errValue = (parsed as { error?: unknown }).error;
                    message = typeof errValue === 'string' ? errValue : body;
                } else {
                    message = typeof parsed === 'string' ? parsed : body;
                }
            } catch {
                message = body;
            }
        }
    } catch (err) {
        // ignore parse errors
    }
    throw new Error(message);
}

export async function listPrintDestinations(): Promise<PrintDestinationDTO[]> {
    const res = await apiFetch('/api/tenant/print/destinations');
    return handleResponse<PrintDestinationDTO[]>(res, 'Impossibile recuperare le stampanti');
}

export async function createPrintDestination(payload: PrintDestinationPayload): Promise<PrintDestinationDTO> {
    const res = await apiFetch('/api/tenant/print/destinations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return handleResponse<PrintDestinationDTO>(res, 'Impossibile creare la stampante');
}

export async function updatePrintDestination(id: number, payload: PrintDestinationPayload): Promise<PrintDestinationDTO> {
    const res = await apiFetch(`/api/tenant/print/destinations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return handleResponse<PrintDestinationDTO>(res, 'Impossibile aggiornare la stampante');
}

export async function deletePrintDestination(id: number): Promise<void> {
    const res = await apiFetch(`/api/tenant/print/destinations/${id}`, {
        method: 'DELETE',
    });
    await handleResponse<unknown>(res, 'Impossibile eliminare la stampante');
}

export async function listPrintTemplates(): Promise<PrintTemplateDTO[]> {
    const res = await apiFetch('/api/tenant/print/templates');
    return handleResponse<PrintTemplateDTO[]>(res, 'Impossibile recuperare i template di stampa');
}

export async function createPrintTemplate(payload: PrintTemplatePayload): Promise<PrintTemplateDTO> {
    const res = await apiFetch('/api/tenant/print/templates', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return handleResponse<PrintTemplateDTO>(res, 'Impossibile creare il template di stampa');
}

export async function updatePrintTemplate(id: number, payload: PrintTemplatePayload): Promise<PrintTemplateDTO> {
    const res = await apiFetch(`/api/tenant/print/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return handleResponse<PrintTemplateDTO>(res, 'Impossibile aggiornare il template di stampa');
}

export async function deletePrintTemplate(id: number): Promise<void> {
    const res = await apiFetch(`/api/tenant/print/templates/${id}`, {
        method: 'DELETE',
    });
    await handleResponse<unknown>(res, 'Impossibile eliminare il template di stampa');
}

export async function manualReprintOrder(orderId: string, payload: ManualPrintPayload): Promise<PrintJobDTO> {
    const res = await apiFetch(`/api/tenant/print/orders/${orderId}/reprint`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return handleResponse<PrintJobDTO>(res, 'Impossibile avviare la ristampa manuale');
}

export async function fetchPrintPreview(orderId: string, params: PrintPreviewParams): Promise<PrintPreviewDTO> {
    const query = new URLSearchParams();
    if (params.channel) {
        query.set('channel', params.channel);
    }
    if (params.destinationType) {
        query.set('destinationType', params.destinationType);
    }
    if (typeof params.templateId === 'number') {
        query.set('templateId', String(params.templateId));
    }
    const suffix = query.toString();
    const url = suffix.length > 0
        ? `/api/tenant/print/orders/${orderId}/preview?${suffix}`
        : `/api/tenant/print/orders/${orderId}/preview`;
    const res = await apiFetch(url);
    return handleResponse<PrintPreviewDTO>(res, 'Impossibile generare l\'anteprima di stampa');
}

export async function fetchPrintPreviewPdf(orderId: string, params: PrintPreviewParams): Promise<Blob> {
    const query = new URLSearchParams();
    if (params.channel) {
        query.set('channel', params.channel);
    }
    if (params.destinationType) {
        query.set('destinationType', params.destinationType);
    }
    if (typeof params.templateId === 'number') {
        query.set('templateId', String(params.templateId));
    }
    const suffix = query.toString();
    const url = suffix.length > 0
        ? `/api/tenant/print/orders/${orderId}/preview.pdf?${suffix}`
        : `/api/tenant/print/orders/${orderId}/preview.pdf`;
    const res = await apiFetch(url, {
        headers: {
            Accept: 'application/pdf, application/json',
        },
    });
    if (!res.ok) {
        let message = "Impossibile generare il PDF dell'anteprima di stampa";
        try {
            const text = await res.text();
            if (text) {
                try {
                    const parsed = JSON.parse(text);
                    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
                        const errValue = (parsed as { error?: unknown }).error;
                        if (typeof errValue === 'string') {
                            message = errValue;
                        } else {
                            message = text;
                        }
                    } else if (typeof parsed === 'string') {
                        message = parsed;
                    } else {
                        message = text;
                    }
                } catch {
                    message = text;
                }
            }
        } catch {
            // ignore parsing errors
        }
        throw new Error(message);
    }
    return res.blob();
}
